import { Owner, Show, createEffect, getOwner, on, runWithOwner, useContext } from 'solid-js';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { Heading } from '../../../ui-components/heading/heading';
import { Text } from '../../../ui-components/text/text';
import { StyledAdmin, StyledPdfDashboard, StyledWrapper } from './pdf-dashboard.style';
import { ContentItem, Searchbar } from '../../search/searchbar-standalone';
import { Section } from '../../../grid-system/section/section';
import { Grid } from '../../../grid-system/grid/grid';
import { EntitySlug, supportedTypes, pdfStore, setPdfStore, Collection, FormattedPostItem } from '../pdf-store';
import { Arrangement } from './arrangement/arrangement';
import { TitleModal } from './title-modal/title-modal';
import { PdfFactory } from '../pdf-factory/pdf-factory';
import { ErrorCatcher } from '../../../tools/error-catcher';
import { CollectionsList } from './collections/collections-list';
import { SeoSettings } from '../../seo-settings/seo-settings';
import { Portal } from 'solid-js/web';
import { gql } from 'graphql-request';
import { unwrap } from 'solid-js/store';
import { fetchPostsByKeys } from '../pdf-fetch-posts';
import { Divider } from '../../../ui-components/divider/divider';

const MUTATION_UPDATE_PDF_COLLECTIONS = gql`
    mutation updatePdfCollections(
        $collections: String!
    ) {
        updatePdfCollections(
            collections: $collections
        ) {
            success
        }
    }
`;

const QUERY_GET_PDF_COLLECTIONS = gql`
    query getPdfCollections {
        getPdfCollections
    }
`;


type DbPost = {
    key: string;
    meta: {
        postTitle: string;
    };
}

const delay = 1000; // Minimum delay for the database update to ensure better UX

export const PdfBuilderDashboard = () => {
    const {
        productsEntityName, 
        blogAndNewsEntityName, 
        eventsEntityName, 
        localize,
        graphQlClient,
        createCachedResource,
        userState,
        viewport,
        ...rest
    } = useContext(AppContext);

    const [ dbCollections, { refetch } ] = createCachedResource(QUERY_GET_PDF_COLLECTIONS, {}, false);

    const owner = getOwner();

    setPdfStore('entitySlugMap', {
        product: productsEntityName,
        'blog-and-news': blogAndNewsEntityName,
        event: eventsEntityName,
    });

    setPdfStore('entityNameMap', {
        product: localize('product', 'Product'),
        'blog-and-news': `${localize('blog', 'Blog')} & ${localize('news', 'News')}`,
        event: localize('event', 'Event'),
        page: localize('page', 'Page'),
    });

    createEffect(() => {
        // Initialize the pdfStore with existing collections from the database
        if (dbCollections()) {            
            const collections = JSON.parse(dbCollections().getPdfCollections);
            setPdfStore('collections', collections);
        }
    });

    createEffect(
        on(
            () => userState?.user,
            () => {
                if (userState.user) {
                    // If the user just logged in, fetch their saved collections (this will also remove current collections created in the logged-out state)
                    refetch();
                }
            },
            { defer: true }
        )
    );

    const showDividerOnSmallerScreens = () => viewport.width <= 900;

    const uploadPdfCollections = async (collections: any) => {          
        try {
            const vars = {
                collections: JSON.stringify(collections),
            };
    
            const response = await graphQlClient(MUTATION_UPDATE_PDF_COLLECTIONS, vars);

            if (!response.updatePdfCollections.success) {
                throw new Error('Failed to update PDF collections');
            }

            return true;
        } catch (error: any) {
            console.error('Error updating PDF collections:', error);
            setPdfStore('status', 'error');
            return false;
        }
    };

    const formatPostItem = (post: ContentItem): FormattedPostItem => {
        const type = post.type as EntitySlug;

        const entityName = pdfStore.entitySlugMap[ type ];
        const key = entityName ? post.url.replace(entityName, type) : post.url;

        return {
            key,
            type,
            postTitle: post.postTitle,
            objectID: post.objectID,
            url: post.url,
            hideOnWeb: false, // Will be false, otherwise it would not be selectable in the search in the first place
            available: true, // Initially assume the post exists, because otherwise it would not be selectable in the search
        };
    };

    const addItem = (originalPost: ContentItem) => {
        const post = formatPostItem(originalPost);

        const externalId = post?.objectID;
        const type = post?.type as EntitySlug;

        if (!externalId || !type) {
            return;
        }

        if (!supportedTypes.includes(type)) {
            return;
        }

        if (type === 'product') {
            const products = [ ...pdfStore.products, externalId ];
            setPdfStore('products', products);
        }

        if (type === 'blog-and-news') {
            const articles = [ ...pdfStore.articles, externalId ];
            setPdfStore('articles', articles);
        }

        const posts = [ ...pdfStore.workingCollectionPosts ];
        posts.push(post);
        setPdfStore('workingCollectionPosts', posts);
    };

    const excludedEndpointsForBuilderList = () => {
        const endpoints = [
            rest.ifuPage.url,
            rest.productsListingPage?.url,        
            rest.eventsListingPage?.url,
            rest.hcpEventsListingPage?.url,
            rest.blogsNewsListingPage?.url,
            rest.hcpBlogsNewsListingPage?.url,
            rest.videosListingPage?.url,
            rest.pdfBuilderPage,
            rest.newsletterSignupPage,
            rest.pageRequestPasswordResetUrl,
            rest.pageResetPasswordUrl,
            rest.pageVerifyAccountUrl,
            rest.shopPage?.url,
            rest.myContentPage?.url,
            rest.page404?.url,
            rest.pageRegistration?.url,
            rest.searchPage?.url,
            rest.productContactFormUrl,
            rest.coursesListingPage?.url,
        ].filter(endpoint => endpoint !== undefined && endpoint !== '');
    
        return endpoints as string[];
    };

    const generateId = () => {
        // Simple hash function for id generation
        const hashString = (str: string) => {
            let hash = 0, i, chr;
            if (str.length === 0) return hash.toString();
            for (i = 0; i < str.length; i++) {
                chr = str.charCodeAt(i);
                hash = ((hash << 5) - hash) + chr;
                hash |= 0; // Convert to 32bit integer
            }
            return Math.abs(hash).toString();
        };

        const now = new Date().toISOString();
        const id = hashString(`${pdfStore.title}-${now}`);
        return id;
    };

    const checkAvailabilityOfPosts = async (posts: FormattedPostItem[]): Promise<FormattedPostItem[]> => {
        // Ensure at least 1 second passes, for better UX
        const minDelay = new Promise(resolve => setTimeout(resolve, delay));
        const dbPostsPromise = fetchPostsByKeys({ graphQlClient: graphQlClient, keys: posts.map(post => post.key) });

        const [dbPosts] = await Promise.all([dbPostsPromise, minDelay]);

        const postsWithExistanceCheck = posts.map((post: FormattedPostItem) => {
            const dbPost = dbPosts.find((dbPost: DbPost) => dbPost.key === post.key);

            return {
                ...post,
                available: Boolean(dbPost) && dbPost?.meta?.extra?.hideOnWeb !== true, // Check if the post exists in the content db and if it is not hidden on the web (product/blogs can be hidden).
                postTitle: dbPost ? dbPost?.meta?.postTitle : post.postTitle, // Use the updated title from the content db if possible.

                // TODO: We should probably update the url here as well, with the correct entity slug, if we want to be able to link
                // from the arrangement item to the actual post (atm we don't)
            };
        });

        return postsWithExistanceCheck;
    };
    
    const createNewCollection = async () => {
        const filteredPosts = removeUnavailablePosts(pdfStore.workingCollectionPosts);

        const newCollection: Collection = {
            id: generateId(),
            title: pdfStore.title,
            orderedPosts: filteredPosts,
            downloaded: false,
            createdAt: new Date().toISOString(),
            siteInfo: {
                siteId: rest.siteInfo.siteId,
                domain: rest.siteInfo.currentDomain,
            }
        };

        const updatedCollections = [ ...pdfStore.collections, newCollection ];

        const storeUpdateCallback = () => setPdfStore('collections', (collections) => [
            ...collections,
            newCollection
        ]);

        await updatePdfCollections(
            updatedCollections,
            storeUpdateCallback,
        );

        return;
    };

    const saveExistingCollection = () => {
        setPdfStore('status', 'updating-existing');

        const unwrappedCollections = unwrap(pdfStore.collections);
        const indexOfCollectionToUpdate = unwrappedCollections.findIndex(collection => collection.id === pdfStore.activeCollectionId);

        if (indexOfCollectionToUpdate === -1) {
            console.error('No existing collection found with the id:', pdfStore.activeCollectionId);
            return;
        }

        const filteredPosts = removeUnavailablePosts(pdfStore.workingCollectionPosts);

        unwrappedCollections[ indexOfCollectionToUpdate ].orderedPosts = [ ...filteredPosts ];
        const updatedCollections = [ ...unwrappedCollections ];

        const storeUpdateCallback = () => setPdfStore(
            'collections', // what
            [ indexOfCollectionToUpdate ], // which
            'orderedPosts', [ ...filteredPosts ] // new value
        );

        updatePdfCollections(
            updatedCollections,
            storeUpdateCallback,
        );
    };

    const deleteCollection = (collectionId: string) => {
        setPdfStore('status', 'deleting');
        setPdfStore('activeCollectionId', collectionId);

        const updatedCollections = pdfStore.collections.filter(collection => collection.id !== collectionId);

        updatePdfCollections(
            updatedCollections,
            () => setPdfStore('collections', updatedCollections)
        );
    };

    const editCollectionAsArrangement = async (collection: Collection) => {
        setPdfStore('workingCollectionPosts', []);
        setPdfStore('status', 'editing');
        setPdfStore('title', collection.title);
        setPdfStore('activeCollectionId', collection.id);

        const postsWithExistanceCheck = await checkAvailabilityOfPosts(collection.orderedPosts);
        
        setPdfStore('workingCollectionPosts', postsWithExistanceCheck);
    };

    const updatePdfCollections = async (updatedCollections: any, updateStoreCallback: () => void) => {
        if (!userState?.user) {
            updateStoreCallback();
            reset();
            return;
        }

        // Ensure at least 1 second passes, for better UX
        const minDelay = new Promise(resolve => setTimeout(resolve, delay));
        const res = await Promise.all([
            uploadPdfCollections(updatedCollections),
            minDelay
        ]).then(([uploadResult]) => uploadResult);

        if (!res) {
            console.error('Failed to update PDF collections in the database');
            setPdfStore('status', 'error');
            return;
        }

        updateStoreCallback();
        reset();

        return;
    };

    const removeUnavailablePosts = (posts: FormattedPostItem[]) => {
        return posts.filter(post => post.available);
    };

    const reset = () => {
        setPdfStore('status', '');
        setPdfStore('title', '');
        setPdfStore('activeCollectionId', '');
        setPdfStore('workingCollectionPosts', []);
    };

    return (
        <ErrorCatcher componentName="Pdf Builder">
            <StyledPdfDashboard>
                <SeoSettings seo={{ noIndex: true }} title={localize('pdf-builder-title', 'PDF Builder')} />
                <StyledAdmin>
                    <Section widthType="bgFull">
                        <Grid templateShorthand={ [ 12 ] }>
                            <StyledWrapper>
                                <div style={{ 'flex-basis': '50%' }}>
                                    <Heading tag="h1" variant="large">
                                        {localize('pdf-builder-title', 'PDF Builder')}
                                    </Heading>

                                    <Text fontSize="normal">
                                        {localize('pdf-builder-description', 'Create a PDF with the products and posts you would like to combine.')}
                                    </Text>

                                    <Searchbar
                                        enabledPostTypes={ supportedTypes }
                                        excludedEndpoints={ excludedEndpointsForBuilderList() }
                                        onPostSelect={ addItem }
                                    />

                                    <Arrangement 
                                        saveExistingCollection={ saveExistingCollection }
                                        reset={ reset }
                                    />

                                    <TitleModal 
                                        createNewCollection={ createNewCollection }
                                        saveExistingCollection={ saveExistingCollection }
                                    />
                                </div>

                                <Show when={showDividerOnSmallerScreens()}>
                                    <Divider />
                                </Show>

                                <div style={{ 'flex-basis': '50%' }}>
                                    <CollectionsList
                                        deleteCollection={deleteCollection}
                                        editCollectionAsArrangement={editCollectionAsArrangement}
                                        reset={reset}
                                    />
                                </div>

                            </StyledWrapper>
                        </Grid>
                    </Section>
                </StyledAdmin>
            </StyledPdfDashboard>
            
            <Portal mount={document.getElementById('pdf-factory-root')!}>
                {runWithOwner(owner as Owner, () => (
                    <PdfFactory />
                ))}
            </Portal>
        </ErrorCatcher>
    );
};

